import { RendererContextType } from '../../../../contexts/RendererContext';
import { ProjectByIdQuery } from '../../../../types/graphqlTypes';
import { isNotNullOrUndefined } from '../../../../utils/isNotNullOrUndefined';
import { AreaAnnotation, PointoramaAnnotation } from '../../../../potree/utils/PointoramaAnnotation';
import { SyncConfig } from './syncFromGraphQL';

type Project = ProjectByIdQuery['projectById'];
type Annotation = Project['state']['annotations'][number];

function getCalculatedVolumes(identifier: string, calculationsByIdentifier: Record<string, Project['calculations']>): number[] {
  return calculationsByIdentifier[identifier]
    ? calculationsByIdentifier[identifier]
        .filter((calculation) => !calculation.isOutDated)
        .map((calculation) => {
          if (calculation.result?.__typename === 'VolumeCalculationResult') {
            return calculation.result.volume;
          }
          return null;
        })
        .filter(isNotNullOrUndefined)
    : [];
}

export function createSyncAreaAnnotationsConfig({
  viewer,
  annotations,
  groups,
  calculationsByIdentifier,
}: {
  viewer: NonNullable<RendererContextType['viewer']>;
  annotations: Annotation[];
  groups: Project['state']['groups'];
  calculationsByIdentifier: Record<string, Project['calculations']>;
}): SyncConfig<Annotation, AreaAnnotation> {
  const groupsByIdentifier = Object.fromEntries(groups.map((g) => [g.identifier, g]));

  return {
    getSourceItems: () => annotations.filter((a) => a.type === 'AREA'),
    getSceneItems: () => viewer.scene.measurements.filter((m): m is AreaAnnotation => m instanceof AreaAnnotation && m.finished),
    getSourceIdentifier: (item) => item.identifier,
    getSceneIdentifier: (item) => item.identifier,
    // update() omitted — geometry updates are handled by reactive property subscriptions
    create: (source, existing) => {
      const calculatedVolumes = getCalculatedVolumes(source.identifier, calculationsByIdentifier);
      const measure = new AreaAnnotation({
        viewer,
        finished: true,
        identifier: source.identifier,
        selected: existing?.selected || false,
      });
      measure.groupName = (source.groupIdentifier && groupsByIdentifier[source.groupIdentifier]?.name) || '';
      measure.addVolumeLabels(calculatedVolumes);
      return measure;
    },
    addToScene: (item) => viewer.scene.addMeasurement(item),
    removeFromScene: (item) => {
      if (item instanceof PointoramaAnnotation) {
        viewer.scene.removeMeasurement(item);
      }
    },
    afterCreate: (item) => {
      item.loadGeometryProperties();
    },
    chunkSize: 50,
  };
}
